// app/javascript/controllers/award_editor_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "canvas", "headerBar", "inputName", "inputTitle",
        "previewNameDisplay", "previewTitleDisplay", "previewSignature",
        "recognitionLine", "previewDate", "starIcon", "confettiOverlay",
        "batchBtn", "batchBtnText",
        "heroName", "downloadBtn", "printBtn"
    ]

    connect() {
        this.updateText()
        this.validateSignatory()
    }

    canvasTargetConnected() {
        // Recipient/filter Turbo navigation replaces the frame, not this controller.
        queueMicrotask(() => {
            if (this.hasCanvasTarget) this.validateSignatory()
        })
    }

    validateSignatory() {
        const name = (this.inputNameTarget?.value || "").trim()
        const title = (this.inputTitleTarget?.value || "").trim()
        const logoUnavailable = this.canvasTarget.dataset.logoUnavailable === "true"
        const ok = name.length > 0 && title.length > 0 && !logoUnavailable

        this.toggleButton(this.batchBtnTarget, ok)
        if (this.hasDownloadBtnTarget) this.toggleButton(this.downloadBtnTarget, ok)
        if (this.hasPrintBtnTarget) this.toggleButton(this.printBtnTarget, ok)
        if (logoUnavailable) {
            [this.batchBtnTarget, ...(this.hasDownloadBtnTarget ? [this.downloadBtnTarget] : []),
                ...(this.hasPrintBtnTarget ? [this.printBtnTarget] : [])]
                .forEach(btn => btn.setAttribute("title", "Please reload to load your charity's logo"))
        }
    }

    async ensureArtworkReady() {
        if (this.canvasTarget.dataset.logoUnavailable === "true") throw new Error("Charity logo unavailable")
        await document.fonts.ready
        await Promise.all([...this.canvasTarget.querySelectorAll("img")].map(img => new Promise((resolve, reject) => {
            const finish = error => {
                clearTimeout(timer)
                img.removeEventListener("load", loaded)
                img.removeEventListener("error", failed)
                error ? reject(error) : resolve()
            }
            const loaded = () => finish(img.naturalWidth > 0 ? null : new Error("Award image unavailable"))
            const failed = () => finish(new Error("Award image unavailable"))
            const timer = setTimeout(() => finish(new Error("Award image timed out")), 10000)
            img.addEventListener("load", loaded, { once: true })
            img.addEventListener("error", failed, { once: true })
            if (img.complete) loaded()
        })))
    }

    reportExportError(error) {
        console.error("Award generation failed:", error)
        alert("We couldn't prepare your award. Please refresh the page and try again. If this continues, contact ECHOage for help.")
    }

    toggleButton(btn, enabled) {
        if (!btn) return
        btn.disabled = !enabled

        if (enabled) {
            btn.classList.remove("opacity-50", "cursor-not-allowed")
            btn.removeAttribute("title")
        } else {
            btn.classList.add("opacity-50", "cursor-not-allowed")
            btn.setAttribute("title", "Please add signatory details")
        }
    }

    async persistSignatory() {
        const name = (this.inputNameTarget?.value || "").trim()
        const title = (this.inputTitleTarget?.value || "").trim()
        if (!name || !title) return false

        const token = document.querySelector('meta[name="csrf-token"]')?.content

        const response = await fetch("/charity/awards/signatory", {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
                "X-CSRF-Token": token
            },
            body: JSON.stringify({
                award_signatory_name: name,
                award_signatory_title: title
            })
        })
        if (!response.ok) throw new Error("Could not save award signatory")

        return true
    }

    updateText() {
        this.previewNameDisplayTarget.textContent = this.inputNameTarget.value
        this.previewSignatureTarget.textContent = this.inputNameTarget.value
        this.previewTitleDisplayTarget.textContent = this.inputTitleTarget.value
    }

    changeStyle(event) {
        const style = event.currentTarget.dataset.style
        const config = {
            classic: { primary: "#00BAE6", accent: "#00BAE6", bg: "#F1F7FB", confetti: false },
            playful: { primary: "#FFB400", accent: "#FFB400", bg: "#FFFBEB", confetti: true  },
            elegant: { primary: "#1A1F2B", accent: "#64748b", bg: "#F8FAFC", confetti: false },
            modern:  { primary: "#FA9EB6", accent: "#BE185D", bg: "#FFF1F2", confetti: false }
        }

        const selected = config[style]

        // 1. Update UI Buttons (Sidebar)
        this.element.querySelectorAll('[data-style]').forEach(btn => {
            btn.classList.remove('border-primary', 'bg-primary/5')
            btn.classList.add('border-slate-50')
            const label = btn.querySelector('span')
            if (label) {
                label.style.color = ""
                label.classList.add('text-slate-400')
                label.classList.remove('text-primary')
            }
        })

        event.currentTarget.classList.replace('border-slate-50', 'border-primary')
        event.currentTarget.classList.add('bg-primary/5')
        const activeLabel = event.currentTarget.querySelector('span')
        if (activeLabel) {
            activeLabel.classList.remove('text-slate-400')
            activeLabel.style.color = selected.primary
        }

// 2. Apply Transitions and Colors (FIXED FOR HTML2CANVAS)

        // Remove Tailwind utility classes that might set oklab/oklch colors
        this.headerBarTarget.classList.remove('bg-primary', 'bg-secondary')
        this.canvasTarget.classList.remove('bg-background-light')

        // Apply explicit HEX colors inline
        // html2canvas reads inline styles perfectly
        this.headerBarTarget.style.backgroundColor = selected.primary
        this.canvasTarget.style.backgroundColor = selected.bg
        this.recognitionLineTarget.style.color = selected.accent

        if (this.hasStarIconTarget) {
            this.starIconTarget.style.transition = "color 0.8s ease-in-out"
            this.starIconTarget.style.color = selected.accent
        }

        // 3. Confetti Overlay Toggle
        if (this.hasConfettiOverlayTarget) {
            if (selected.confetti) {
                this.confettiOverlayTarget.classList.replace('opacity-0', 'opacity-100')
            } else {
                this.confettiOverlayTarget.classList.replace('opacity-100', 'opacity-0')
            }
        }
    }

    async download() {
        try {
            await this.ensureArtworkReady()
            if (!(await this.persistSignatory())) return
            const currentBg = this.canvasTarget.style.backgroundColor || "#F1F7FB";
            const dataUrl = await htmlToImage.toPng(this.canvasTarget, {
                quality: 1.0,
                pixelRatio: 3,
                backgroundColor: currentBg, // Ensures no transparency issues
                skipAutoScale: true,
                cacheBust: true,
                // This filter function removes elements you don't want captured (optional)
                filter: (node) => !node.classList?.contains('exclude-from-capture')
            })

            const link = document.createElement('a')
            link.download = `ECHOage-Award-${Date.now()}.png`
            link.href = dataUrl
            link.click()

        } catch (error) {
            this.reportExportError(error)
        }
    }

    async batchDownload() {
        const originalText = this.batchBtnTextTarget.innerText
        const originalName = this.heroNameTarget.innerText
        const originalDate = this.previewDateTarget.innerText
        this.batchBtnTarget.disabled = true
        this.batchBtnTarget.classList.add('opacity-75', 'cursor-not-allowed')
        try {
            await this.ensureArtworkReady()
            if (!(await this.persistSignatory())) return
            const params = new URLSearchParams()

            const rangeSelect = this.element.querySelector('select[name="range"]')
            if (rangeSelect?.value) params.set("range", rangeSelect.value)

            const searchInput = this.element.querySelector('input[name="search"]')
            if (searchInput?.value) params.set("search", searchInput.value)

            const partyId = this.element.querySelector('[data-active-party-id]')?.dataset?.activePartyId
            if (partyId) params.set("party_id", partyId)

            const response = await fetch(`${window.location.pathname}.json?${params.toString()}`)
            if (!response.ok) throw new Error("Could not load award recipients")
            const recipients = await response.json()

            if (recipients.length === 0) throw new Error("No recipients found")

            const zip = new JSZip()
            const folder = zip.folder("ECHOage_Awards")
            zip.file("ECHOage_Award_Contacts.csv", this.buildContactsCsv(recipients))
            const currentBg = this.canvasTarget.style.backgroundColor || "#F1F7FB";
            const isConfetti = this.element.querySelector('[data-style="playful"]').classList.contains('border-primary')

            for (let i = 0; i < recipients.length; i++) {
                const r = recipients[i]
                this.batchBtnTextTarget.innerText = `GENERATING ${i + 1}/${recipients.length}`

// 1) Recipient-specific fields
                this.heroNameTarget.innerText = r.name
                this.previewDateTarget.innerText = r.date

// 2) Signatory fields (constant across the batch)
                this.previewNameDisplayTarget.innerText = this.inputNameTarget.value
                this.previewSignatureTarget.innerText = this.inputNameTarget.value
                this.previewTitleDisplayTarget.innerText = this.inputTitleTarget.value

                // --- AVATAR LOGIC START ---
                const avatarImg = this.canvasTarget.querySelector('img[alt="Hero Avatar"]')
                if (avatarImg) {
                    const gender = (r.gender || 'm').toLowerCase()
                    const isGirl = gender === 'female' || gender === 'f'

                    // 1. CACHE BUSTER: ?t=... forces a fresh network request to S3
                    // This bypasses the "stale" non-CORS cache in the browser
                    const cacheBuster = `?t=${new Date().getTime()}`

                    const newSrc = isGirl
                        ? `https://cdn.echoage.com/transparent_avatar_girl.png${cacheBuster}`
                        : `https://cdn.echoage.com/transparent_avatar_boy.png${cacheBuster}`

                    // 2. EXPLICIT CROSSORIGIN: As you suggested, we force it here
                    avatarImg.crossOrigin = "anonymous"

                    // Only wait if we are changing the image
                    if (avatarImg.src !== newSrc) {
                        await new Promise(resolve => {
                            avatarImg.onload = resolve
                            avatarImg.onerror = () => {
                                console.warn("Avatar failed to load:", newSrc)
                                resolve() // Proceed anyway to avoid freezing
                            }
                            // Shorter timeout for batch efficiency
                            setTimeout(resolve, 1500)

                            avatarImg.src = newSrc
                        })
                    }
                }
                // --- AVATAR LOGIC END ---

                await new Promise(r => setTimeout(r, 50)) // Tiny paint delay

                await this.ensureArtworkReady()
                const dataUrl = await htmlToImage.toPng(this.canvasTarget, {
                    quality: 1.0,
                    pixelRatio: 2,
                    skipAutoScale: true,
                    cacheBust: true,
                    backgroundColor: isConfetti ? "#FFFBEB" : currentBg
                })
                const response = await fetch(dataUrl)
                const blob = await response.blob()
                folder.file(this.awardFilename(r.name), blob)
            }

            this.batchBtnTextTarget.innerText = "ZIPPING..."
            const content = await zip.generateAsync({ type: "blob" })

            const link = document.createElement('a')
            link.href = URL.createObjectURL(content)
            link.download = `ECHOage_Batch_Awards_${new Date().toISOString().slice(0,10)}.zip`
            link.click()

        } catch (error) {
            this.reportExportError(error)
        } finally {
            this.heroNameTarget.innerText = originalName
            this.previewDateTarget.innerText = originalDate
            this.batchBtnTextTarget.innerText = originalText
            this.batchBtnTarget.disabled = false
            this.batchBtnTarget.classList.remove('opacity-75', 'cursor-not-allowed')
            this.validateSignatory()
        }
    }

    buildContactsCsv(recipients) {
        const headers = ["Child Name", "Host Name", "Host Email", "Party Date", "Award Filename"]
        const rows = recipients.map((recipient) => [
            recipient.name,
            recipient.host_name,
            recipient.host_email,
            recipient.date,
            this.awardFilename(recipient.name)
        ])

        return "\uFEFF" + [headers, ...rows]
            .map((row) => row.map((value) => this.csvCell(value)).join(","))
            .join("\r\n")
    }

    csvCell(value) {
        let text = String(value ?? "")
        if (/^[=+\-@]/.test(text)) text = `'${text}`
        return `"${text.replace(/"/g, '""')}"`
    }

    awardFilename(name) {
        const safeName = String(name || "Recipient").replace(/[^a-z0-9]/gi, "_")
        return `${safeName}_Award.png`
    }

    async print() {
        // Open synchronously with the click so Safari does not block the window
        // while fonts/artwork and the signatory save are being awaited.
        const printWindow = window.open('', '', 'width=800,height=1000')
        try {
            if (!printWindow) throw new Error("Print window blocked")
            await this.ensureArtworkReady()
            if (!(await this.persistSignatory())) {
                printWindow.close()
                return
            }
            // Use the same renderer as downloads; this page loads html-to-image,
            // not the old html2canvas global.
            const dataUrl = await htmlToImage.toPng(this.canvasTarget, {
                pixelRatio: 2,
                skipAutoScale: true,
                backgroundColor: this.canvasTarget.style.backgroundColor || "#F1F7FB"
            })

            printWindow.document.write(`
                <html>
                    <body style="margin:0; padding:0;">
                        <img src="${dataUrl}" style="width:100%;">
                    </body>
                </html>
            `)
            printWindow.document.close()
            await printWindow.document.querySelector("img").decode()
            printWindow.onafterprint = () => printWindow.close()
            printWindow.focus()
            printWindow.print()
        } catch (error) {
            printWindow?.close()
            this.reportExportError(error)
        }
    }
}
